import { Component, OnInit, OnDestroy } from "@angular/core";
import { formatUtcDate } from "../../shared/date-utils";
import { EntraUserSyncService, EntraUserSyncStatus, EntraUserSyncJobQueueStatus, EntraGraphTestResponse, RoleSyncConfig } from "../../services/entra-user-sync.service";
import { interval, Subscription } from "rxjs";
import { switchMap, takeWhile } from "rxjs/operators";
import { CommonModule } from "@angular/common";
import { FormsModule } from "@angular/forms";
import { MaterialModule } from "../../material.module";
import { LeftMenuComponent } from "../../menu/left-menu/left-menu.component";
import { SpinnerComponent } from "../../shared/spinner/spinner.component";
import { UsersSyncSkeletonComponent } from "./users-sync-skeleton/users-sync-skeleton.component";
import { UsersSyncConfigSkeletonComponent } from "./users-sync-config-skeleton/users-sync-config-skeleton.component";

@Component({
  selector: "modernization-users-sync",
  templateUrl: "./users-sync.component.html",
  styleUrls: ['./users-sync.component.scss'],
  imports: [CommonModule, FormsModule, MaterialModule, LeftMenuComponent, SpinnerComponent, UsersSyncSkeletonComponent, UsersSyncConfigSkeletonComponent]
})
export class UsersSyncComponent implements OnInit, OnDestroy {
  status: EntraUserSyncStatus | null = null;
  jobStatus: EntraUserSyncJobQueueStatus | null = null;
  loading = false;
  triggering = false;
  error: string | null = null;
  successMessage: string | null = null;

  // DB-backed config for global staleness trigger
  config: RoleSyncConfig | null = null;
  configLoading = false;
  configSaving = false;
  configError: string | null = null;
  configSuccessMessage: string | null = null;
  staleThresholdHours = 24;
  private loadedThresholdHours = 24;

  // Temporary debug panel for CMID test endpoint
  debugGroupName = '';
  debugDelegated = true;
  debugFirstPageOnly = true;
  debugTopResults = 50;
  debugTesting = false;
  debugTestResult: EntraGraphTestResponse | null = null;
  debugError: string | null = null;
  
  private statusSubscription?: Subscription;
  private pollingSubscription?: Subscription;

  constructor(private entraUserSyncService: EntraUserSyncService) {}

  ngOnInit(): void {
    this.loadStatus();
    this.loadConfig();
  }

  ngOnDestroy(): void {
    this.statusSubscription?.unsubscribe();
    this.pollingSubscription?.unsubscribe();
  }

  loadStatus(): void {
    this.loading = true;
    this.error = null;

    this.entraUserSyncService.getStatusWithJob().subscribe({
      next: ({ status, job }) => {
        this.status = status;
        this.jobStatus = job;
        this.loading = false;

        // Start polling if sync is running OR a job is queued/running
        if ((status.state === 'Running' || job.isQueuedOrRunning) && !this.pollingSubscription) {
          this.startPolling();
        }
      },
      error: (err) => {
        console.error('Error loading sync status:', err);
        this.error = 'Failed to load sync status';
        this.loading = false;
      }
    });
  }

  loadConfig(): void {
    this.configLoading = true;
    this.configError = null;
    this.configSuccessMessage = null;

    this.entraUserSyncService.getRoleSyncConfig().subscribe({
      next: (cfg) => {
        this.config = cfg;
        this.staleThresholdHours = cfg?.staleThresholdHours ?? 24;
        this.loadedThresholdHours = this.staleThresholdHours;
        this.configLoading = false;
      },
      error: (err) => {
        console.error('Error loading role sync config:', err);
        this.configError = 'Failed to load role sync config';
        this.configLoading = false;
      }
    });
  }

  get hasConfigChanges(): boolean {
    return this.staleThresholdHours !== this.loadedThresholdHours;
  }

  saveConfig(): void {
    if (!this.hasConfigChanges) {
      return;
    }

    const hours = Number(this.staleThresholdHours);
    if (!Number.isFinite(hours) || hours < 1) {
      this.configError = 'Stale threshold must be at least 1 hour';
      return;
    }

    this.configSaving = true;
    this.configError = null;
    this.configSuccessMessage = null;

    this.entraUserSyncService.updateRoleSyncConfig(hours).subscribe({
      next: (cfg) => {
        this.config = cfg;
        this.staleThresholdHours = cfg?.staleThresholdHours ?? hours;
        this.loadedThresholdHours = this.staleThresholdHours;
        this.configSuccessMessage = 'Role sync threshold saved';
        this.configSaving = false;
      },
      error: (err) => {
        console.error('Error saving role sync config:', err);
        this.configError = err?.error?.message || 'Failed to save role sync config';
        this.configSaving = false;
      }
    });
  }

  resetConfig(): void {
    this.staleThresholdHours = this.loadedThresholdHours;
    this.configError = null;
    this.configSuccessMessage = null;
  }

  startPolling(): void {
    // Poll every 2 seconds while sync is running
    this.pollingSubscription = interval(2000)
      .pipe(
        switchMap(() => this.entraUserSyncService.getStatusWithJob()),
        takeWhile(({ status, job }) => status.state === 'Running' || job.isQueuedOrRunning, true) // inclusive
      )
      .subscribe({
        next: ({ status, job }) => {
          this.status = status;
          this.jobStatus = job;

          // Stop polling when sync completes and queue is idle
          if (status.state !== 'Running' && !job.isQueuedOrRunning) {
            this.pollingSubscription?.unsubscribe();
            this.pollingSubscription = undefined;
          }
        },
        error: (err) => {
          console.error('Error polling sync status:', err);
          this.pollingSubscription?.unsubscribe();
          this.pollingSubscription = undefined;
        }
      });
  }

  triggerSync(): void {
    if (this.status?.state === 'Running' || this.jobStatus?.isQueuedOrRunning) {
      return;
    }

    this.triggering = true;
    this.error = null;
    this.successMessage = null;

    this.entraUserSyncService.triggerSync().subscribe({
      next: (response) => {
        this.successMessage = 'Sync job queued successfully!';
        this.triggering = false;
        // Start polling immediately
        this.loadStatus();
      },
      error: (err) => {
        console.error('Error triggering sync:', err);
        if (err.status === 409) {
          this.error = 'Sync is already queued or running';
        } else {
          this.error = err.error?.message || 'Failed to trigger sync';
        }
        this.triggering = false;
      }
    });
  }

  testGraph(): void {
    this.debugError = null;
    this.debugTestResult = null;

    const group = (this.debugGroupName || '').trim();
    if (!group) {
      this.debugError = 'Provide a group display name or object id.';
      return;
    }

    this.debugTesting = true;
    this.entraUserSyncService
      .testGraph(group, this.debugDelegated, this.debugFirstPageOnly, this.debugTopResults)
      .subscribe({
        next: (result) => {
          this.debugTestResult = result;
          this.debugTesting = false;
        },
        error: (err) => {
          console.error('Error testing Graph:', err);
          this.debugError = err?.error?.detail || err?.error?.message || 'Test failed';
          this.debugTesting = false;
        }
      });
  }

  getProgressPercentage(): number {
    if (!this.status || this.status.groupsTotal === 0) {
      return 0;
    }
    return Math.round((this.status.groupsProcessed / this.status.groupsTotal) * 100);
  }

  formatDuration(duration: string): string {
    if (!duration) return 'N/A';
    
    // Parse TimeSpan string (format: HH:MM:SS.mmmmmmm)
    const parts = duration.split(':');
    if (parts.length < 3) return duration;
    
    const hours = parseInt(parts[0]);
    const minutes = parseInt(parts[1]);
    const seconds = Math.floor(parseFloat(parts[2]));
    
    if (hours > 0) {
      return `${hours}h ${minutes}m ${seconds}s`;
    } else if (minutes > 0) {
      return `${minutes}m ${seconds}s`;
    } else {
      return `${seconds}s`;
    }
  }

  formatDate(date: string | Date | null): string {
    if (!date) {
      return 'N/A';
    }
    const d = date instanceof Date ? date : new Date(date);
    return formatUtcDate(d, 'short');
  }

  getStateClass(): string {
    if (!this.status) return '';
    
    switch (this.status.state) {
      case 'Running': return 'badge-primary';
      case 'Completed': return 'badge-success';
      case 'Failed': return 'badge-danger';
      case 'Idle': return 'badge-secondary';
      default: return '';
    }
  }

  getStateIcon(): string {
    if (!this.status) return 'info';
    
    switch (this.status.state) {
      case 'Running': return 'sync';
      case 'Completed': return 'check_circle';
      case 'Failed': return 'error';
      case 'Idle': return 'schedule';
      default: return 'info';
    }
  }
}
